import { useEffect, useState } from 'react';
import { settingsAPI } from '../api';

const CACHE_KEY = 'cinefora_settings';

// Helper function to adjust color brightness
const adjustColorBrightness = (hex, percent) => {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Convert to RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Adjust brightness
  r = Math.max(0, Math.min(255, r + (r * percent / 100)));
  g = Math.max(0, Math.min(255, g + (g * percent / 100)));
  b = Math.max(0, Math.min(255, b + (b * percent / 100)));
  
  // Convert back to hex
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
};

const applyColorsToCSS = (colors) => {
  document.documentElement.style.setProperty('--color-primary', colors.primary_color);
  // Calculate hover color (darker version of primary)
  const primary = colors.primary_color || '#fdb714';
  const hoverColor = adjustColorBrightness(primary, -20);
  document.documentElement.style.setProperty('--color-primary-hover', hoverColor);
  
  document.documentElement.style.setProperty('--color-now-showing', colors.now_showing_badge_color);
  document.documentElement.style.setProperty('--color-coming-soon', colors.coming_soon_badge_color);
  document.documentElement.style.setProperty('--color-bg-primary', colors.background_primary);
  document.documentElement.style.setProperty('--color-bg-secondary', colors.background_secondary);
  document.documentElement.style.setProperty('--color-text-primary', colors.text_primary);
  document.documentElement.style.setProperty('--color-text-secondary', colors.text_secondary);
  document.documentElement.style.setProperty('--color-text-muted', colors.text_muted);
  
  // Gradient colors
  document.documentElement.style.setProperty('--gradient-movie-from', colors.gradient_movie_from || '#3b82f6');
  document.documentElement.style.setProperty('--gradient-movie-to', colors.gradient_movie_to || '#8b5cf6');
  document.documentElement.style.setProperty('--gradient-salon-from', colors.gradient_salon_from || '#10b981');
  document.documentElement.style.setProperty('--gradient-salon-to', colors.gradient_salon_to || '#f59e0b');
  document.documentElement.style.setProperty('--gradient-campaign-from', colors.gradient_campaign_from || '#a855f7');
  document.documentElement.style.setProperty('--gradient-campaign-to', colors.gradient_campaign_to || '#ec4899');
  document.documentElement.style.setProperty('--gradient-showtime-from', colors.gradient_showtime_from || '#f59e0b');
  document.documentElement.style.setProperty('--gradient-showtime-to', colors.gradient_showtime_to || '#ef4444');
  document.documentElement.style.setProperty('--gradient-news-from', colors.gradient_news_from || '#3b82f6');
  document.documentElement.style.setProperty('--gradient-news-to', colors.gradient_news_to || '#8b5cf6');
};

export const useThemeColors = () => {
  const [colors, setColors] = useState(() => {
    // Try to load from localStorage first
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const settings = JSON.parse(cached);
        const cachedColors = {
          primary_color: settings.primary_color || '#fdb714',
          now_showing_badge_color: settings.now_showing_badge_color || '#fdb714',
          coming_soon_badge_color: settings.coming_soon_badge_color || '#9333ea',
          background_primary: settings.background_primary || '#000000',
          background_secondary: settings.background_secondary || '#0a0a0a',
          text_primary: settings.text_primary || '#ffffff',
          text_secondary: settings.text_secondary || '#d1d5db',
          text_muted: settings.text_muted || '#9ca3af',
          gradient_movie_from: settings.gradient_movie_from || '#3b82f6',
          gradient_movie_to: settings.gradient_movie_to || '#8b5cf6',
          gradient_salon_from: settings.gradient_salon_from || '#10b981',
          gradient_salon_to: settings.gradient_salon_to || '#f59e0b',
          gradient_campaign_from: settings.gradient_campaign_from || '#a855f7',
          gradient_campaign_to: settings.gradient_campaign_to || '#ec4899',
          gradient_showtime_from: settings.gradient_showtime_from || '#f59e0b',
          gradient_showtime_to: settings.gradient_showtime_to || '#ef4444',
          gradient_news_from: settings.gradient_news_from || '#3b82f6',
          gradient_news_to: settings.gradient_news_to || '#8b5cf6',
        };
        // Apply colors immediately from cache
        applyColorsToCSS(cachedColors);
        return cachedColors;
      }
    } catch (error) {
      console.error('Error loading cached colors:', error);
    }
    
    // Default colors if cache not available
    return {
      primary_color: '#fdb714',
      now_showing_badge_color: '#fdb714',
      coming_soon_badge_color: '#9333ea',
      background_primary: '#000000',
      background_secondary: '#0a0a0a',
      text_primary: '#ffffff',
      text_secondary: '#d1d5db',
      text_muted: '#9ca3af',
      gradient_movie_from: '#3b82f6',
      gradient_movie_to: '#8b5cf6',
      gradient_salon_from: '#10b981',
      gradient_salon_to: '#f59e0b',
      gradient_campaign_from: '#a855f7',
      gradient_campaign_to: '#ec4899',
      gradient_showtime_from: '#f59e0b',
      gradient_showtime_to: '#ef4444',
      gradient_news_from: '#3b82f6',
      gradient_news_to: '#8b5cf6',
    };
  });

  useEffect(() => {
    const loadColors = async () => {
      try {
        const response = await settingsAPI.get();
        const settings = response.data;
        
        // Cache the entire settings object
        localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
        
        const newColors = {
          primary_color: settings.primary_color || '#fdb714',
          now_showing_badge_color: settings.now_showing_badge_color || '#fdb714',
          coming_soon_badge_color: settings.coming_soon_badge_color || '#9333ea',
          background_primary: settings.background_primary || '#000000',
          background_secondary: settings.background_secondary || '#0a0a0a',
          text_primary: settings.text_primary || '#ffffff',
          text_secondary: settings.text_secondary || '#d1d5db',
          text_muted: settings.text_muted || '#9ca3af',
          gradient_movie_from: settings.gradient_movie_from || '#3b82f6',
          gradient_movie_to: settings.gradient_movie_to || '#8b5cf6',
          gradient_salon_from: settings.gradient_salon_from || '#10b981',
          gradient_salon_to: settings.gradient_salon_to || '#f59e0b',
          gradient_campaign_from: settings.gradient_campaign_from || '#a855f7',
          gradient_campaign_to: settings.gradient_campaign_to || '#ec4899',
          gradient_showtime_from: settings.gradient_showtime_from || '#f59e0b',
          gradient_showtime_to: settings.gradient_showtime_to || '#ef4444',
          gradient_news_from: settings.gradient_news_from || '#3b82f6',
          gradient_news_to: settings.gradient_news_to || '#8b5cf6',
        };
        
        setColors(newColors);
        applyColorsToCSS(newColors);
      } catch (error) {
        console.error('Error loading theme colors:', error);
      }
    };

    loadColors();
  }, []);

  return colors;
};
